import React from 'react'
import {Box, Flex, Select, Text} from '../../../utils/chakraMonomorphic'

import {
  PersonSeasonPhotos,
  PersonSeasonTagStats,
  PersonTaggedPhoto,
} from '../../../types'
import copy from '../copy.json'
import {PhotoStrip} from './PhotoStrip'

interface Props {
  seasons: PersonSeasonTagStats[]
  selectedSeason: number | null
  onSelectSeason: (season: number) => void
  photos: PersonSeasonPhotos
  clientId: number
  canLinkAlbums: boolean
}

interface StatProps {
  label: string
  value: number
  testId: string
  photos: PersonTaggedPhoto[]
  clientId: number
  stripTestId: string
  canLinkAlbums: boolean
}

// Each count carries its own thumbnail strip so the number can be spot-checked
// against the actual photos rather than taken on faith.
const Stat: React.FC<StatProps> = ({
  label,
  value,
  testId,
  photos,
  clientId,
  stripTestId,
  canLinkAlbums,
}) => (
  <Box mr={10} mb={4}>
    <Text fontSize='sm' color='gray.700'>
      {label}
    </Text>
    <Text fontSize='1.6rem' fontWeight='bold' data-testid={testId}>
      {value}
    </Text>
    <PhotoStrip
      photos={photos}
      clientId={clientId}
      testId={stripTestId}
      canLinkAlbums={canLinkAlbums}
    />
  </Box>
)

export const SeasonTagStats: React.FC<Props> = ({
  seasons,
  selectedSeason,
  onSelectSeason,
  photos,
  clientId,
  canLinkAlbums,
}): React.ReactElement => {
  const current = seasons.find((s) => s.season === selectedSeason) ?? null

  return (
    <Box mb={8} data-testid='season-tag-stats'>
      <Flex alignItems='center' mb={3} gap={3} wrap='wrap'>
        <Text fontSize='1.05rem' fontWeight='bold'>
          {copy.stats.heading}
        </Text>
        {/* Every season with data comes back on the initial response, so
            switching seasons re-fetches only to refresh the close-call list —
            the counts below are already in hand. */}
        <Select
          size='sm'
          maxW='140px'
          aria-label={copy.lookup.seasonLabel}
          value={selectedSeason ?? ''}
          onChange={(e): void => onSelectSeason(Number(e.target.value))}
          data-testid='season-select'>
          {seasons.map((s) => (
            <option key={s.season} value={s.season}>
              {s.season}
            </option>
          ))}
        </Select>
      </Flex>

      {!current ? (
        <Text fontSize='sm' color='gray.700' data-testid='season-no-data'>
          {copy.states.noSeasonData}
        </Text>
      ) : (
        <Flex wrap='wrap'>
          <Stat
            label={copy.stats.photosTagged}
            value={current.photosTagged}
            testId='stat-photos-tagged'
            photos={photos.all}
            clientId={clientId}
            stripTestId='photo-strip-all'
            canLinkAlbums={canLinkAlbums}
          />
          <Stat
            label={copy.stats.frTags}
            value={current.frTags}
            testId='stat-fr-tags'
            photos={photos.fr}
            clientId={clientId}
            stripTestId='photo-strip-fr'
            canLinkAlbums={canLinkAlbums}
          />
          <Stat
            label={copy.stats.manualTags}
            value={current.manualTags}
            testId='stat-manual-tags'
            photos={photos.manual}
            clientId={clientId}
            stripTestId='photo-strip-manual'
            canLinkAlbums={canLinkAlbums}
          />
        </Flex>
      )}

      <Text fontSize='xs' color='gray.700' mt={2}>
        {copy.stats.note} {copy.photos.note}
      </Text>
    </Box>
  )
}
